import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
  Grid,
} from "@mui/material";
import { tokens } from "../../theme";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useAuth from "../../hooks/useAuth";

const AdminTransferTab = ({ userId, accounts }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const axiosPrivate = useAxiosPrivate();
  const { success, snackError } = useAuth();

  const [activeSubTab, setActiveSubTab] = useState(0);
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form Fields State
  const [fromAccount, setFromAccount] = useState("");
  const [toAccount, setToAccount] = useState("");
  const [toAccountName, setToAccountName] = useState("");
  const [selectedBank, setSelectedBank] = useState("");
  const [amount, setAmount] = useState("");
  const [narration, setNarration] = useState("");
  const [makerReason, setMakerReason] = useState("");
  const [expiryHours, setExpiryHours] = useState(24);

  // Verification States
  const [verifyingAccount, setVerifyingAccount] = useState(false);

  const handleSubTabChange = (event, newValue) => {
    setActiveSubTab(newValue);
    // Reset values on tab change
    setFromAccount("");
    setToAccount("");
    setToAccountName("");
    setSelectedBank("");
    setAmount("");
    setNarration("");
    setMakerReason("");
    setExpiryHours(24);
  };

  // Fetch commercial banks for External Bank Transfers
  const getCommercialBanks = useCallback(async () => {
    setLoadingBanks(true);
    try {
      const response = await axiosPrivate.get(
        "/transaction-payment/api/v1/transactions/commercial-banks"
      );
      if (response?.data?.success || response?.data?.status === true) {
        setBanks(response?.data?.data || []);
      } else {
        setBanks(response?.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch commercial banks:", error);
      snackError("Failed to load commercial banks.");
    } finally {
      setLoadingBanks(false);
    }
  }, [axiosPrivate, snackError]);

  useEffect(() => {
    if (activeSubTab === 1) {
      getCommercialBanks();
    }
  }, [activeSubTab, getCommercialBanks]);

  // Same Bank Enquiry Lookup
  const handleVerifySameBank = async (accountNo) => {
    if (!accountNo || accountNo.length !== 10) return;
    setVerifyingAccount(true);
    try {
      const res = await axiosPrivate.get(
        `/auth-serviceV3/api/v1/accounts/account/${accountNo}/enquiry`
      );
      const name = res?.data?.data?.accountName || res?.data?.accountName;
      if (name) {
        setToAccountName(name);
        success("Same Bank Account verified successfully!");
      } else {
        setToAccountName("");
        snackError("Could not retrieve account name.");
      }
    } catch (error) {
      setToAccountName("");
      snackError(error?.response?.data?.message || "Failed to verify account.");
    } finally {
      setVerifyingAccount(false);
    }
  };

  // External Bank Enquiry Lookup
  const handleVerifyExternalBank = async () => {
    if (!toAccount || !selectedBank) {
      snackError("Please select a bank and enter an account number first.");
      return;
    }
    setVerifyingAccount(true);
    try {
      const payload = { accountNumber: toAccount, bankCode: selectedBank };
      const res = await axiosPrivate.post(
        "/transaction-payment/api/v1/transactions/name-enquiry",
        payload
      );
      if (res?.data?.success || res?.data?.status === true) {
        if (res?.data?.data?.successful) {
          const name = res?.data?.data?.name;
          if (name) {
            setToAccountName(name);
            success("External Account verified successfully!");
          } else {
            snackError("Could not retrieve account name.");
            setToAccountName("");
          }
        } else {
          snackError(res?.data?.data?.responseMessage || "Failed to verify account.");
          setToAccountName("");
        }
      } else {
        setToAccountName("");
        snackError("Failed to verify account.");
      }
    } catch (error) {
      setToAccountName("");
      snackError(error?.response?.data?.message || "An error occurred during verification.");
    } finally {
      setVerifyingAccount(false);
    }
  };

  // Submit Handler
  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!fromAccount || !amount || !narration || !makerReason) {
      snackError("Please fill out all required fields.");
      return;
    }

    if (activeSubTab !== 2 && !toAccount) {
      snackError("Please specify the recipient account.");
      return;
    }

    if (activeSubTab !== 2 && !toAccountName) {
      snackError("Please verify the recipient account name first.");
      return;
    }

    setSubmitting(true);
    const reference = "REF-" + Date.now();

    try {
      let endpoint = "";
      let payload = {
        userId: String(userId),
        amount: Number(amount),
        narration,
        makerReason,
        expiryHours: Number(expiryHours) || 24,
        reference,
      };

      if (activeSubTab === 0 || activeSubTab === 2) {
        // Same Bank or Self Transfer
        endpoint = "/transaction-payment/api/v1/admin/maker-checker/maker/intra-bank-transfer";
        const finalToAccountName = activeSubTab === 2
          ? (accounts.find(w => w.accountNumber === toAccount)?.accountName || "")
          : toAccountName;

        payload = {
          ...payload,
          fromAccountNumber: fromAccount,
          toAccountNumber: activeSubTab === 2 ? toAccount : toAccount,
          toAccountName: finalToAccountName,
        };
      } else {
        // External Transfer
        endpoint = "/transaction-payment/api/v1/admin/maker-checker/maker/inter-bank-transfer";
        const payerName = accounts.find(w => w.accountNumber === fromAccount)?.accountName || "";

        payload = {
          ...payload,
          payerAccountNumber: fromAccount,
          payer: payerName,
          receiverBankCode: selectedBank,
          receiverAccountNumber: toAccount,
          receiverName: toAccountName,
        };
      }

      const response = await axiosPrivate.post(endpoint, payload);

      if (response?.data?.success || response?.data?.status === true) {
        success("Transfer request submitted successfully (Maker initiated)!");
        // Reset form
        setToAccount("");
        setToAccountName("");
        setAmount("");
        setNarration("");
        setMakerReason("");
      } else {
        snackError(response?.data?.message || "Transfer initiation failed.");
      }
    } catch (error) {
      console.error("Transfer error:", error);
      snackError(error?.response?.data?.message || "An error occurred during transfer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <Tabs
        value={activeSubTab}
        onChange={handleSubTabChange}
        textColor="secondary"
        indicatorColor="secondary"
        centered
        sx={{ mb: 2 }}
      >
        <Tab label="Same Bank" />
        <Tab label="External Bank" />
        <Tab label="Transfer to Self" />
      </Tabs>

      <form onSubmit={handleTransferSubmit}>
        <Grid container spacing={2}>
          {/* Source Account (All Tabs) */}
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="From Account"
              variant="filled"
              value={fromAccount}
              onChange={(e) => setFromAccount(e.target.value)}
              required
            >
              {accounts.map((acc, index) => (
                <MenuItem key={index} value={acc.accountNumber}>
                  {acc.accountName} ({acc.accountNumber}) - ₦{acc.availableBalance}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Same Bank Destination Account Number */}
          {activeSubTab === 0 && (
            <>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="To Account Number"
                  variant="filled"
                  value={toAccount}
                  onChange={(e) => {
                    const val = e.target.value;
                    setToAccount(val);
                    if (val.length === 10) {
                      handleVerifySameBank(val);
                    }
                  }}
                  inputProps={{ maxLength: 10 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} display="flex" alignItems="center">
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleVerifySameBank(toAccount)}
                  disabled={verifyingAccount || toAccount.length !== 10}
                  sx={{ height: "56px" }}
                >
                  {verifyingAccount ? <CircularProgress size={24} /> : "Verify"}
                </Button>
              </Grid>
            </>
          )}

          {/* External Bank Fields */}
          {activeSubTab === 1 && (
            <>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Receiver Bank"
                  variant="filled"
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  disabled={loadingBanks}
                  required
                >
                  {loadingBanks ? (
                    <MenuItem value="">Loading Banks...</MenuItem>
                  ) : (
                    banks.map((bank, index) => (
                      <MenuItem key={index} value={bank.Code || bank.code}>
                        {bank.Name || bank.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Receiver Account Number"
                  variant="filled"
                  value={toAccount}
                  onChange={(e) => setToAccount(e.target.value)}
                  inputProps={{ maxLength: 10 }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} display="flex" alignItems="center">
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={handleVerifyExternalBank}
                  disabled={verifyingAccount || toAccount.length !== 10 || !selectedBank}
                  sx={{ height: "56px" }}
                >
                  {verifyingAccount ? <CircularProgress size={24} /> : "Verify"}
                </Button>
              </Grid>
            </>
          )}

          {/* Transfer to Self Destination Account */}
          {activeSubTab === 2 && (
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Destination Account (Own Wallet)"
                variant="filled"
                value={toAccount}
                onChange={(e) => {
                  setToAccount(e.target.value);
                  setToAccountName(accounts.find(w => w.accountNumber === e.target.value)?.accountName || "");
                }}
                required
              >
                {accounts
                  .filter((acc) => acc.accountNumber !== fromAccount)
                  .map((acc, index) => (
                    <MenuItem key={index} value={acc.accountNumber}>
                      {acc.accountName} ({acc.accountNumber})
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
          )}

          {/* Receiver Account Name (For Same Bank and External Bank tabs) */}
          {activeSubTab !== 2 && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Receiver Account Name"
                variant="filled"
                value={toAccountName}
                disabled
                helperText={toAccountName ? "Account name verified" : "Enter account number and click Verify"}
              />
            </Grid>
          )}

          {/* Amount, Narration, Reason, Expiry */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              variant="filled"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Expiry Hours"
              type="number"
              variant="filled"
              value={expiryHours}
              onChange={(e) => setExpiryHours(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Narration"
              variant="filled"
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reason for Transfer (makerReason)"
              variant="filled"
              value={makerReason}
              onChange={(e) => setMakerReason(e.target.value)}
              required
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              type="submit"
              color="secondary"
              variant="contained"
              fullWidth
              disabled={submitting}
              sx={{ py: 1.5, mt: 1 }}
            >
              {submitting ? <CircularProgress size={24} /> : "Initiate Transfer Request"}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default AdminTransferTab;
